import type { TaskTiptapParseInput, TaskUtils } from '@Core/lib/tasks/utils/types';
import { decodeHtmlString, isEncodedHtmlString } from '@Pages/pageRuntime/lib/htmlString';
import { sanitizeContentHtml } from '@Pages/pageRuntime/lib/sanitizeHtml';

// Render stored TipTap JSON without importing the editor stack into public pages.

type TiptapMark = {
  type?: string;
  attrs?: Record<string, unknown> | null;
};

type TiptapNode = {
  type?: string;
  text?: string | null;
  attrs?: Record<string, unknown> | null;
  content?: TiptapNode[];
  marks?: TiptapMark[];
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function normalizeTiptapContent(content: unknown): TiptapNode | string | null {
  return typeof content === 'string' || (typeof content === 'object' && content !== null)
    ? (content as TiptapNode | string)
    : null;
}

function renderTiptapText(node: TiptapNode | string | null): string {
  if (typeof node === 'string') {
    return node;
  }

  if (!node) {
    return '';
  }

  if (node.type === 'text') {
    return node.text ?? '';
  }

  if (node.type === 'workflowVariable') {
    return String(node.attrs?.value ?? '');
  }

  if (node.type === 'contentMedia') {
    return String(node.attrs?.fileName || 'File');
  }

  if (node.type === 'hardBreak') {
    return '\n';
  }

  const content = (node.content ?? []).map(renderTiptapText).join('');

  return node.type === 'paragraph' ? `${content}\n` : content;
}

export function tiptapPlainText(content: unknown): string {
  return renderTiptapText(normalizeTiptapContent(content)).trimEnd();
}

function applyTiptapMark(html: string, mark: TiptapMark): string {
  switch (mark.type) {
    case 'bold':
      return `<strong>${html}</strong>`;
    case 'italic':
      return `<em>${html}</em>`;
    case 'strike':
      return `<s>${html}</s>`;
    case 'underline':
      return `<u>${html}</u>`;
    case 'code':
      return `<code>${html}</code>`;
    case 'link': {
      const href = escapeHtml(String(mark.attrs?.href ?? ''));
      const target = mark.attrs?.target ? ` target="${escapeHtml(String(mark.attrs.target))}"` : '';
      const rel = mark.attrs?.rel ? ` rel="${escapeHtml(String(mark.attrs.rel))}"` : '';

      return href ? `<a href="${href}"${target}${rel}>${html}</a>` : html;
    }
    default:
      return html;
  }
}

function applyTiptapMarks(html: string, marks: TiptapMark[] | undefined): string {
  return (marks ?? []).reduce((current, mark) => applyTiptapMark(current, mark), html);
}

function renderTiptapHtml(node: TiptapNode | string | null): string {
  if (typeof node === 'string') {
    return escapeHtml(node);
  }

  if (!node) {
    return '';
  }

  if (node.type === 'text') {
    return applyTiptapMarks(escapeHtml(node.text ?? ''), node.marks);
  }

  if (node.type === 'workflowVariable') {
    const value = node.attrs?.value;

    if (isEncodedHtmlString(value)) {
      const decodedHtml = decodeHtmlString(value);
      if (decodedHtml) {
        return `<span data-type="workflow-variable">${sanitizeContentHtml(decodedHtml)}</span>`;
      }
    }

    return `<span data-type="workflow-variable">${escapeHtml(String(value ?? ''))}</span>`;
  }

  if (node.type === 'contentMedia') {
    const fileId = escapeHtml(String(node.attrs?.fileId ?? ''));
    const fileName = escapeHtml(String(node.attrs?.fileName ?? ''));
    const fileMimeType = escapeHtml(String(node.attrs?.fileMimeType ?? ''));

    return `<div data-type="content-media" data-file-id="${fileId}" data-file-name="${fileName}" data-file-mime-type="${fileMimeType}"></div>`;
  }

  const content = (node.content ?? []).map(renderTiptapHtml).join('');

  switch (node.type) {
    case 'doc':
      return content;
    case 'paragraph':
      return `<p>${content}</p>`;
    case 'heading': {
      const level = Number(node.attrs?.level ?? 1);
      const safeLevel = level >= 1 && level <= 6 ? level : 1;

      return `<h${safeLevel}>${content}</h${safeLevel}>`;
    }
    case 'bulletList':
      return `<ul>${content}</ul>`;
    case 'orderedList':
      return `<ol>${content}</ol>`;
    case 'listItem':
      return `<li>${content}</li>`;
    case 'blockquote':
      return `<blockquote>${content}</blockquote>`;
    case 'codeBlock':
      return `<pre><code>${content}</code></pre>`;
    case 'hardBreak':
      return '<br>';
    case 'horizontalRule':
      return '<hr>';
    default:
      return content;
  }
}

function parseTiptap(input: TaskTiptapParseInput): string {
  const content = normalizeTiptapContent(input?.content);

  if (!content) {
    return '';
  }

  if (input?.html ?? false) {
    const html = renderTiptapHtml(content);

    return html ? sanitizeContentHtml(html) : '';
  }

  const text = renderTiptapText(content);
  const parsed = input?.oneLine ? text.replace(/\s+/g, ' ').trim() : text.trimEnd();

  return parsed || input?.placeholder || '';
}

export const tiptapUtils: TaskUtils['tiptap'] = {
  parse: parseTiptap
};
